<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getToolLabel,
  getToolCompletedLabel,
} from 'shared/utils/copilotToolLabels';

const props = defineProps({
  toolCall: { type: Object, required: true },
});

const { t } = useI18n();
const isExpanded = ref(false);

const isRunning = computed(() =>
  ['pending', 'running', 'in_progress', 'started', 'queued'].includes(
    props.toolCall.status
  )
);

const isComplete = computed(() =>
  [
    'finished',
    'complete',
    'completed',
    'done',
    'success',
    'succeeded',
  ].includes(props.toolCall.status)
);

const isError = computed(() =>
  ['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(
    props.toolCall.status
  )
);

const statusIcon = computed(() => {
  if (isRunning.value) return 'i-lucide-wrench';
  if (isError.value) return 'i-lucide-x-circle';
  return 'i-lucide-check';
});

const statusColor = computed(() => {
  if (isRunning.value) return 'text-n-slate-11';
  if (isError.value) return 'text-n-ruby-11';
  return 'text-n-green-11';
});

const displayLabel = computed(() => {
  if (isComplete.value) return getToolCompletedLabel(props.toolCall.name);
  if (isError.value) return t('COPILOT.TOOL_ERROR');
  return getToolLabel(props.toolCall.name);
});

// Extract a brief context hint from tool args (e.g., query, name, id)
const progressHint = computed(() => {
  if (!isRunning.value) return '';
  const args = props.toolCall.args;
  if (!args || typeof args !== 'object') return '';
  // Look for common arg patterns that give context
  const hint =
    args.query || args.name || args.search || args.title || args.email;
  if (!hint || typeof hint !== 'string') return '';
  return hint.length > 40 ? hint.slice(0, 40) + '...' : hint;
});

const toolArgs = computed(() => {
  const args = props.toolCall.args;
  if (!args) return null;
  if (typeof args === 'string') {
    try {
      return JSON.parse(args);
    } catch {
      return args;
    }
  }
  return args;
});

const toolResult = computed(() => {
  const result = props.toolCall.result;
  if (!result) return null;
  if (typeof result === 'string') {
    try {
      return JSON.parse(result);
    } catch {
      return result;
    }
  }
  return result;
});

const hasDetails = computed(() => !!toolArgs.value || !!toolResult.value);

function formatJSON(obj) {
  if (typeof obj === 'string') return obj;
  return JSON.stringify(obj, null, 2);
}
</script>

<template>
  <div class="relative py-1.5 group">
    <button
      class="flex items-center gap-2 p-0 text-left text-sm leading-5 font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
      :class="{ 'cursor-default': !hasDetails }"
      @click="hasDetails && (isExpanded = !isExpanded)"
    >
      <div
        class="absolute right-full mr-2 top-1.5 flex items-center pointer-events-none"
      >
        <div
          class="size-4 shrink-0"
          :class="[
            statusIcon,
            statusColor,
            isRunning ? 'animate-wrench-turn' : '',
          ]"
        />
      </div>
      <span>{{ displayLabel }}</span>
      <span
        v-if="progressHint"
        class="text-sm font-normal text-n-slate-10 truncate max-w-48"
      >
        — {{ progressHint }}
      </span>
      <div
        v-if="hasDetails"
        class="i-lucide-chevron-right size-3 text-n-slate-9 transition-transform opacity-0 group-hover:opacity-100"
        :class="{ 'rotate-90': isExpanded }"
      />
    </button>

    <!-- Expanded details (progressive disclosure) -->
    <div
      v-if="isExpanded && hasDetails"
      class="mt-1.5 pl-0 rounded-md border border-n-weak bg-n-solid-2 overflow-hidden"
    >
      <!-- Arguments -->
      <div v-if="toolArgs" class="px-3 py-2">
        <p
          class="text-[10px] uppercase tracking-wider font-medium text-n-slate-9 mb-1"
        >
          {{ t('COPILOT.TOOL_ARGS') }}
        </p>
        <pre
          class="text-xs leading-relaxed text-n-slate-12 overflow-x-auto max-h-32 whitespace-pre-wrap"
          >{{ formatJSON(toolArgs) }}</pre>
      </div>

      <!-- Result -->
      <div v-if="toolResult" class="px-3 py-2 border-t border-n-weak">
        <p
          class="text-[10px] uppercase tracking-wider font-medium text-n-slate-9 mb-1"
        >
          {{ t('COPILOT.TOOL_RESULT') }}
        </p>
        <pre
          class="text-xs leading-relaxed text-n-slate-12 overflow-x-auto max-h-48 whitespace-pre-wrap"
          >{{ formatJSON(toolResult) }}</pre>
      </div>
    </div>
  </div>
</template>
